import { useCallback, useEffect, useState } from 'react'
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore'
import { auth, db, normalizeCode } from '../lib/firebase'
import type { EventSummary, Session } from '../types/session'
import {
  clearSession,
  loadOrCreateGuestId,
  loadSession,
  saveSession,
} from '../utils/storage'

function message(error: unknown): string {
  const code = (error as { code?: string })?.code ?? ''
  if (code.includes('invalid-credential') || code.includes('wrong-password')) {
    return 'That email and password combination did not work.'
  }
  if (code.includes('user-not-found')) return 'No administrator account found for that email.'
  if (code.includes('too-many-requests')) return 'Too many attempts. Try again shortly.'
  if (code.includes('operation-not-allowed')) {
    return 'Enable Email/Password sign-in in the Firebase console.'
  }
  if (code.includes('permission-denied')) {
    return 'Firestore rules rejected this request. Publish firestore.rules.'
  }
  if (code.includes('network')) return 'Network error reaching Firebase.'
  return (error as Error)?.message ?? 'Something went wrong. Try again.'
}

export function useSession() {
  const [session, setSession] = useState<Session | null>(null)
  const [ready, setReady] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(
    () =>
      onAuthStateChanged(auth, (user) => {
        const stored = loadSession()
        // Participants never sign in — their session restores on its own.
        if (stored?.role === 'participant') {
          setSession(stored)
        } else if (user && stored && stored.uid === user.uid) {
          setSession(stored)
        } else {
          if (stored) clearSession()
          setSession(null)
        }
        setReady(true)
      }),
    [],
  )

  const commit = useCallback((next: Session) => {
    saveSession(next)
    setSession(next)
  }, [])

  const joinAsParticipant = useCallback(
    async (rawCode: string, rawName: string) => {
      const roomCode = normalizeCode(rawCode)
      const name = rawName.trim().slice(0, 40)
      if (!roomCode || !name) return
      setBusy(true)
      setError(null)
      try {
        // No account, no sign-in — just a device id, the code, and a name.
        const uid = loadOrCreateGuestId()
        const room = await getDoc(doc(db, 'rooms', roomCode))
        if (!room.exists()) {
          setError(
            `No event found for code ${roomCode}. Check the invitation code with your host.`,
          )
          return
        }
        if (room.data()?.closed === true) {
          setError(
            `“${String(room.data()?.name ?? 'This event')}” is closed. The host has ended this event.`,
          )
          return
        }
        const meRef = doc(db, 'rooms', roomCode, 'participants', uid)
        const existing = await getDoc(meRef)
        if (existing.exists() && existing.data().blocked === true) {
          setError('The host has removed you from this event.')
          return
        }
        if (existing.exists()) {
          await setDoc(meRef, { name, seenAt: serverTimestamp() }, { merge: true })
        } else {
          await setDoc(meRef, {
            name,
            muted: false,
            blocked: false,
            joinedAt: serverTimestamp(),
          })
        }
        commit({
          role: 'participant',
          uid,
          name,
          roomCode,
          eventName: String(room.data()?.name ?? 'Live draw'),
        })
      } catch (err) {
        setError(message(err))
      } finally {
        setBusy(false)
      }
    },
    [commit],
  )

  /** Administrators sign in with an account only — no invitation code. */
  const loginAsHost = useCallback(
    async (email: string, password: string) => {
      if (!email.trim() || !password) return
      setBusy(true)
      setError(null)
      try {
        const cred = await signInWithEmailAndPassword(auth, email.trim(), password)
        commit({
          role: 'admin',
          uid: cred.user.uid,
          name: cred.user.email ?? 'Administrator',
          roomCode: null,
        })
      } catch (err) {
        setError(message(err))
      } finally {
        setBusy(false)
      }
    },
    [commit],
  )

  const listEvents = useCallback(async (): Promise<EventSummary[]> => {
    const uid = auth.currentUser?.uid
    if (!uid) return []
    try {
      const snap = await getDocs(query(collection(db, 'rooms'), where('hostUid', '==', uid)))
      return snap.docs
        .map((d) => ({
          code: d.id,
          name: String(d.data().name ?? 'Untitled event'),
          chatEnabled: d.data().chatEnabled !== false,
          openedAt: d.data().openedAt?.toMillis?.() ?? 0,
        }))
        .sort((a, b) => b.openedAt - a.openedAt)
    } catch {
      return []
    }
  }, [])

  const openEvent = useCallback(
    async (
      rawCode: string,
      eventName: string,
      chatEnabled: boolean,
      startsAt?: number | null,
      timeZone?: string | null,
    ) => {
      const roomCode = normalizeCode(rawCode)
      const name = eventName.trim().slice(0, 60) || 'Live draw'
      const uid = auth.currentUser?.uid
      if (!roomCode || !uid) return
      setBusy(true)
      setError(null)
      try {
        const existing = await getDoc(doc(db, 'rooms', roomCode))
        if (existing.exists() && existing.data().hostUid !== uid) {
          setError('That invitation code is already used by another event.')
          return
        }
        await setDoc(
          doc(db, 'rooms', roomCode),
          {
            code: roomCode,
            name,
            hostUid: uid,
            chatEnabled,
            closed: false,
            openedAt: serverTimestamp(),
            // Only touch the schedule when the setup form supplied one, so
            // reopening an event keeps its existing start time.
            ...(startsAt !== undefined
              ? { startsAt, timeZone: timeZone ?? null }
              : {}),
          },
          { merge: true },
        )
        commit({
          role: 'admin',
          uid,
          name: auth.currentUser?.email ?? 'Administrator',
          roomCode,
          eventName: name,
        })
      } catch (err) {
        setError(message(err))
      } finally {
        setBusy(false)
      }
    },
    [commit],
  )

  const closeEvent = useCallback(() => {
    setSession((prev) => {
      if (!prev) return prev
      const next: Session = { ...prev, roomCode: null, eventName: undefined }
      saveSession(next)
      return next
    })
  }, [])

  const renameEvent = useCallback((eventName: string) => {
    setSession((prev) => {
      if (!prev) return prev
      const next: Session = { ...prev, eventName }
      saveSession(next)
      return next
    })
  }, [])

  const leave = useCallback(async () => {
    clearSession()
    setSession(null)
    if (auth.currentUser) {
      try {
        await signOut(auth)
      } catch {
        /* ignore */
      }
    }
  }, [])

  return {
    session,
    ready,
    busy,
    error,
    setError,
    joinAsParticipant,
    loginAsHost,
    listEvents,
    openEvent,
    closeEvent,
    renameEvent,
    leave,
  }
}
